import React, { useState } from 'react';
import {
  Card, Typography, Switch, Skeleton, App, Input, Button, Space, Tag, Radio,
  Alert, Empty, Popconfirm,
} from 'antd';
import {
  BankOutlined, CreditCardOutlined, CheckCircleOutlined, PlusOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import {
  useGetGetnetConfigQuery,
  useSaveGetnetConfigMutation,
  GetnetTerminal,
} from '../services/getnetApi';

// Mismo canal de soporte que login/registro y la landing
const SUPPORT_WHATSAPP_URL = 'https://wa.me/522206294774';

const { Title, Text } = Typography;

/**
 * Configuración > Terminal Getnet (Santander). A diferencia de MP no hay
 * credenciales por restaurante: la licencia de integración es de la
 * plataforma. El restaurante solo da de alta los números de serie de sus
 * terminales G-Smart, elige la predeterminada y activa el cobro.
 */
const GetnetSettings: React.FC = () => {
  const { isMobile } = useResponsive();
  const { message } = App.useApp();

  const { data: config, isLoading } = useGetGetnetConfigQuery();
  const [saveConfig, { isLoading: saving }] = useSaveGetnetConfigMutation();

  const [newSerial, setNewSerial] = useState('');
  const [newName, setNewName] = useState('');

  const extractError = (err: unknown): string =>
    (err as { data?: { message?: string } })?.data?.message
    ?? 'No se pudo completar la operación. Intenta de nuevo.';

  const saveTerminals = async (terminals: GetnetTerminal[], successMsg: string) => {
    try {
      await saveConfig({ terminals }).unwrap();
      message.success(successMsg);
    } catch (err) {
      message.error(extractError(err));
    }
  };

  const handleAddTerminal = async () => {
    const serial = newSerial.trim();
    if (!serial) {
      message.info('Captura el número de serie de la terminal');
      return;
    }
    const current = config?.terminals ?? [];
    if (current.some((t) => t.serial.toLowerCase() === serial.toLowerCase())) {
      message.warning('Esa terminal ya está dada de alta');
      return;
    }
    await saveTerminals(
      [...current, { serial, name: newName.trim() || `Terminal ${current.length + 1}` }],
      'Terminal agregada',
    );
    setNewSerial('');
    setNewName('');
  };

  const handleRemoveTerminal = async (serial: string) => {
    const current = config?.terminals ?? [];
    await saveTerminals(current.filter((t) => t.serial !== serial), 'Terminal eliminada');
  };

  const handleSetDefault = async (serial: string) => {
    try {
      await saveConfig({ defaultTerminalSerial: serial }).unwrap();
      message.success('Terminal predeterminada actualizada');
    } catch (err) {
      message.error(extractError(err));
    }
  };

  const handleToggleEnabled = async (checked: boolean) => {
    try {
      await saveConfig({ enabled: checked }).unwrap();
      message.success(checked ? 'Cobro con terminal Getnet activado' : 'Cobro con terminal Getnet desactivado');
    } catch (err) {
      message.error(extractError(err));
    }
  };

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <BankOutlined style={{ color: '#ec0000' }} />
          Terminal Getnet
          {config?.environment === 'qa' && <Tag color="orange">Ambiente de pruebas</Tag>}
        </Title>
        <Text type="secondary">
          Cobra las cuentas enviando el pago directo a tu terminal G-Smart de Santander
        </Text>
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%', maxWidth: 860 }}>
        {/* Activación */}
        <Card style={{ borderRadius: isMobile ? 8 : 12 }}>
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 1 }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <CheckCircleOutlined style={{ fontSize: 22, color: 'var(--color-primary)', marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: 15 }}>Cobro con terminal Getnet</Text>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Al activarlo, en el modal de cobro aparece la opción de terminal: el monto
                    llega solo a la terminal, el comensal paga ahí, y el sistema registra el
                    pago y cierra la cuenta automáticamente.
                  </Text>
                </div>
              </div>
              <Switch
                checked={config?.enabled ?? false}
                onChange={handleToggleEnabled}
                loading={saving}
                disabled={!config?.isConfigured && !(config?.enabled)}
                checkedChildren="Sí"
                unCheckedChildren="No"
              />
            </div>
          )}
          {!isLoading && !config?.licenseConfigured && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="La plataforma aún no tiene lista la conexión con Getnet."
              description={
                <Space direction="vertical" size={6}>
                  <Text style={{ fontSize: 13 }}>
                    Contáctanos para habilitarla — no necesitas credenciales propias, solo tus terminales.
                  </Text>
                  <Button
                    size="small"
                    type="primary"
                    style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                    onClick={() => window.open(SUPPORT_WHATSAPP_URL, '_blank', 'noopener')}
                  >
                    Contactar a soporte
                  </Button>
                </Space>
              }
            />
          )}
          {!isLoading && config?.licenseConfigured && !config?.isConfigured && (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message="Da de alta al menos una terminal (número de serie) para poder activar el cobro."
            />
          )}
        </Card>

        {/* Terminales */}
        <Card
          title={<Space><CreditCardOutlined /> Terminales</Space>}
          style={{ borderRadius: isMobile ? 8 : 12 }}
        >
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : (
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                El número de serie viene en la etiqueta de tu terminal G-Smart (también aparece
                en la app de Getnet). La predeterminada recibe los cobros cuando no se elige otra.
              </Text>

              {(config?.terminals?.length ?? 0) === 0 ? (
                <Empty description="Aún no has dado de alta ninguna terminal" />
              ) : (
                <Radio.Group
                  value={config?.defaultTerminalSerial}
                  onChange={(e) => handleSetDefault(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {config!.terminals.map((t) => (
                      <div
                        key={t.serial}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '10px 12px',
                          border: '1px solid var(--border-color, #d9d9d9)',
                          borderRadius: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Radio value={t.serial}>
                          <Space size={8} wrap>
                            <Text strong>{t.name}</Text>
                            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 13 }}>{t.serial}</Text>
                          </Space>
                        </Radio>
                        <div style={{ flex: 1 }} />
                        <Popconfirm
                          title="¿Quitar esta terminal?"
                          description="Los cobros en curso no se afectan; solo deja de estar disponible."
                          okText="Quitar"
                          cancelText="Cancelar"
                          onConfirm={() => handleRemoveTerminal(t.serial)}
                        >
                          <Button size="small" danger icon={<DeleteOutlined />} loading={saving} />
                        </Popconfirm>
                      </div>
                    ))}
                  </Space>
                </Radio.Group>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Input
                  style={{ flex: 2, minWidth: 180 }}
                  placeholder="Número de serie (ej. PP352719091036982)"
                  value={newSerial}
                  onChange={(e) => setNewSerial(e.target.value)}
                />
                <Input
                  style={{ flex: 1, minWidth: 120 }}
                  placeholder="Nombre (ej. Caja 1)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTerminal} loading={saving}>
                  Agregar
                </Button>
              </div>
            </Space>
          )}
        </Card>
      </Space>
    </div>
  );
};

export default GetnetSettings;
